import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { PageHeader } from "@/components/Badges";

const PAIRS = ["EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "NZD/USD", "USD/CAD", "EUR/GBP", "EUR/JPY", "GBP/JPY", "EUR/AUD", "GBP/AUD", "AUD/JPY", "CAD/JPY", "EUR/CAD"];
const RISKS = ["0.25", "0.5", "1", "1.5", "2"];

export default function Calculator() {
  const [f, setF] = useState({ balance: "10000", risk_pct: "1", symbol: "EUR/USD", entry: "", stop_loss: "", tp1: "", tp2: "", tp3: "", account_currency: "USD" });
  const [custom, setCustom] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k, v) => setF({ ...f, [k]: v });
  const prefill = async (sym) => {
    try {
      const { data } = await api.get(`/analysis/${sym.replace("/", "")}`);
      setF((p) => ({ ...p, symbol: sym, entry: String(data.entry), stop_loss: String(data.stop_loss), tp1: String(data.tp1), tp2: String(data.tp2), tp3: String(data.tp3) }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { prefill("EUR/USD"); }, []);
  const calc = async () => {
    try {
      const body = { ...f, balance: Number(f.balance), risk_pct: Number(f.risk_pct), entry: Number(f.entry), stop_loss: Number(f.stop_loss) };
      ["tp1", "tp2", "tp3"].forEach((k) => (body[k] = f[k] ? Number(f[k]) : null));
      setRes((await api.post("/calculator/position-size", body)).data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "w-full bg-white border border-zinc-200 rounded-sm px-3 py-2 num text-sm text-zinc-900 focus:outline-none focus:border-[#007AFF]";
  const F = ({ k, label }) => (
    <label className="space-y-1"><span className="label-xs">{label}</span><input className={inp} type="number" step="any" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`calc-${k}-input`} /></label>
  );
  return (
    <div data-testid="calculator-page">
      <PageHeader title="Position Size Calculator" sub="Risk-first sizing · standard lot = 100,000 units" />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-6 panel p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {F({ k: "balance", label: "Account Balance" })}
            <label className="space-y-1"><span className="label-xs">Account Currency</span>
              <select className={inp} value={f.account_currency} onChange={(e) => set("account_currency", e.target.value)} data-testid="calc-currency-select"><option>USD</option><option>EUR</option><option>GBP</option></select></label>
          </div>
          <div className="space-y-1"><span className="label-xs">Risk per trade</span>
            <div className="flex flex-wrap gap-2">
              {RISKS.map((r) => <button key={r} onClick={() => { setCustom(false); set("risk_pct", r); }} data-testid={`calc-risk-${r}`}
                className={`px-3 py-1.5 text-xs num rounded-sm border transition-colors ${!custom && f.risk_pct === r ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>{r}%</button>)}
              <button onClick={() => setCustom(true)} data-testid="calc-risk-custom" className={`px-3 py-1.5 text-xs rounded-sm border ${custom ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-zinc-200 text-zinc-500"}`}>Custom</button>
              {custom && <input className={`${inp} w-24`} type="number" step="0.05" value={f.risk_pct} onChange={(e) => set("risk_pct", e.target.value)} data-testid="calc-risk-custom-input" />}
            </div>
            {Number(f.risk_pct) > 2 && <p className="flex items-center gap-1.5 text-xs text-[#D97706] mt-2" data-testid="calc-risk-warning"><AlertTriangle size={13} />Risking more than 2% per trade is aggressive.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="label-xs">Currency Pair</span>
              <select className={inp} value={f.symbol} onChange={(e) => prefill(e.target.value)} data-testid="calc-symbol-select">{PAIRS.map((p) => <option key={p}>{p}</option>)}</select></label>
            {F({ k: "entry", label: "Entry" })}
            {F({ k: "stop_loss", label: "Stop Loss" })}
            {F({ k: "tp1", label: "TP1 (optional)" })}
            {F({ k: "tp2", label: "TP2 (optional)" })}
            {F({ k: "tp3", label: "TP3 (optional)" })}
          </div>
          <p className="text-[11px] text-zinc-500">Pre-filled with Fx Cotrader's current plan for the selected pair. Edit freely.</p>
          <button onClick={calc} className="w-full py-2.5 bg-[#007AFF] hover:bg-[#1a86ff] active:scale-[0.99] transition-[background-color,transform] rounded-sm text-sm font-semibold text-white" data-testid="calc-submit-btn">Calculate</button>
        </div>
        <div className="col-span-12 lg:col-span-6 panel p-5" data-testid="calc-results">
          {!res ? <p className="text-sm text-zinc-500">Enter your parameters and press Calculate.</p> : (
            <div className="fade-up">
              <p className="label-xs mb-1">Suggested position size</p>
              <p className="num text-5xl font-semibold text-zinc-900" data-testid="calc-lots">{res.lots} <span className="text-lg text-zinc-500">lots</span></p>
              <p className="num text-xs text-zinc-500 mt-1">{res.units.toLocaleString()} units · {res.direction}</p>
              {res.warning && <p className="mt-3 text-xs text-[#D97706] flex gap-1.5" data-testid="calc-result-warning"><AlertTriangle size={13} />{res.warning}</p>}
              <div className="grid grid-cols-2 gap-3 mt-5">
                {[["Amount at risk", money(res.risk_amount, res.currency), "calc-risk-amount"], ["Stop distance", `${res.sl_pips} pips`, "calc-sl-pips"],
                  ["Est. loss if SL hits", money(-res.estimated_loss, res.currency), "calc-est-loss"], ["Pip value / lot", money(res.pip_value_per_lot, res.currency), "calc-pip-value"]].map(([l, v, t]) => (
                  <div key={l} className="border border-zinc-200 p-3"><p className="label-xs mb-1">{l}</p><p className={`num text-lg ${l.startsWith("Est") ? "text-[#DC2626]" : "text-zinc-900"}`} data-testid={t}>{v}</p></div>
                ))}
              </div>
              {res.targets.length > 0 && <div className="mt-4">{res.targets.map((t) => (
                <div key={t.level} className="flex justify-between py-2 border-b border-zinc-100 text-sm" data-testid={`calc-target-${t.level}`}>
                  <span className="text-zinc-500">{t.level} <span className="num text-xs text-zinc-400">{t.pips}p · 1:{t.rr}</span></span>
                  <span className="num text-[#059669]">+{money(t.profit, res.currency)}</span>
                </div>))}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
